import {
  Box,
  InputAdornment,
  Link as MuiLink,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { supportApi, type ListConversationsParams } from "../client.js";
import { SupportEmptyState, SupportErrorState } from "../components/SupportEmptyState.js";
import { PageLoader } from "@/components/ui/page-loader";
import { Pagination } from "../../../components/ui/pagination.js";
import { relativeTime } from "../../../lib/relativeTime.js";
import { SupportTable } from "../components/SupportTable.js";
import type { SxStyle } from "../../sales/leads/heatStyles.js";

const PER_PAGE = 25;

/**
 * Conversations list — every Otto turn captured from the chat-bot, with
 * customer + org attribution. Filters and search live in the URL so a
 * shared link reproduces the same view. Click a row to read the full
 * transcript and (optionally) flag or escalate it into a ticket.
 */
export function ConversationsListPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const q        = params.get("q")        ?? undefined;
  const persona  = params.get("persona")  ?? undefined;
  const status   = params.get("status")   ?? undefined;
  const flagged  = params.get("flagged")  ?? undefined;
  const page     = params.get("page")     ?? "1";

  const conversationsQuery = useQuery({
    queryKey: ["support", "conversations", { q, persona, status, flagged, page }],
    queryFn: () => {
      const p: ListConversationsParams = { page, per_page: String(PER_PAGE) };
      if (q)       p.q = q;
      if (persona) p.persona = persona;
      if (status)  p.status = status;
      if (flagged) p.flagged = flagged;
      return supportApi.listConversations(p);
    },
    staleTime: 15_000,
  });

  function setParam(key: string, value: string | null): void {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("page");
    setParams(next, { replace: true });
  }

  return (
    <Box component="section" sx={{ display: "grid", gap: 2 }}>
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { md: "1fr auto auto auto" },
          alignItems: { md: "center" },
        }}
      >
        <SearchInput
          value={q ?? ""}
          onCommit={(v) => setParam("q", v || null)}
          placeholder="Search transcripts — refund, login, pricing…"
        />
        <FilterSelect
          value={persona ?? ""}
          onChange={(v) => setParam("persona", v || null)}
          placeholder="Persona"
          options={[
            { value: "support", label: "Support" },
            { value: "general", label: "General" },
          ]}
        />
        <FilterSelect
          value={status ?? ""}
          onChange={(v) => setParam("status", v || null)}
          placeholder="Status"
          options={[
            { value: "active",     label: "Active" },
            { value: "flagged",    label: "Flagged" },
            { value: "escalated",  label: "Escalated" },
            { value: "archived",   label: "Archived" },
          ]}
        />
        <FilterSelect
          value={flagged === "true" ? "true" : ""}
          onChange={(v) => setParam("flagged", v || null)}
          placeholder="Flagged only"
          options={[{ value: "true", label: "Flagged only" }]}
        />
      </Box>

      {conversationsQuery.isLoading && <PageLoader />}
      {conversationsQuery.isError && (
        <SupportErrorState
          error={conversationsQuery.error}
          resourceLabel="conversations"
        />
      )}

      {conversationsQuery.data && conversationsQuery.data.data.length === 0 && (
        <SupportEmptyState
          title={q ? "No conversations match this search" : "No conversations yet"}
          description={
            q
              ? "Try a different search term or clear the filters above."
              : "Once customers chat with Otto, every turn shows up here with full attribution."
          }
        />
      )}

      {conversationsQuery.data && conversationsQuery.data.data.length > 0 && (
        <SupportTable
          head={
            <TableRow>
              <TableCell align="left">Last activity</TableCell>
              <TableCell align="left">Org</TableCell>
              <TableCell align="left">User</TableCell>
              <TableCell align="left">Persona</TableCell>
              <TableCell align="right">Turns</TableCell>
              <TableCell align="left">Last user message</TableCell>
              <TableCell align="left">Status</TableCell>
              <TableCell align="right">
                <Box component="span" sx={visuallyHidden}>Actions</Box>
              </TableCell>
            </TableRow>
          }
          empty={null}
        >
          {conversationsQuery.data.data.map((c) => (
            <TableRow
              key={c.id}
              sx={{
                borderTop: 1,
                borderColor: "divider",
                transition: "background-color 150ms",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              <TableCell sx={{ color: "text.disabled" }}>
                <MuiLink component={Link} to={`/support/conversations/${c.id}`} color="inherit" underline="hover">
                  {relativeTime(c.last_activity_at)}
                </MuiLink>
              </TableCell>
              <TableCell>
                <MuiLink component={Link} to={`/support/organizations/${c.organization.id}`} color="inherit" underline="hover">
                  {c.organization.name}
                </MuiLink>
              </TableCell>
              <TableCell>
                <Box sx={{ fontWeight: 500 }}>{c.user.name}</Box>
                <Box sx={{ fontSize: 12, color: "text.disabled" }}>{c.user.email}</Box>
              </TableCell>
              <TableCell sx={{ fontSize: 12, color: "text.disabled" }}>
                {c.persona}
              </TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{c.turn_count}</TableCell>
              <TableCell sx={{ maxWidth: "28rem" }}>
                <MuiLink
                  component={Link}
                  to={`/support/conversations/${c.id}`}
                  color="text.primary"
                  underline="hover"
                  title={c.last_user_message ?? undefined}
                  sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {c.last_user_message ?? "—"}
                </MuiLink>
              </TableCell>
              <TableCell>
                <ConversationStatusPill status={c.status} />
              </TableCell>
              <TableCell align="right">
                <MuiLink
                  component={Link}
                  to={`/support/conversations/${c.id}`}
                  aria-label={`View conversation with ${c.user.name}`}
                  underline="none"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    borderRadius: 999,
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "surfaceSoft",
                    px: 1.5,
                    py: 0.5,
                    fontSize: 12,
                    fontWeight: 500,
                    color: "text.primary",
                    "&:hover": { borderColor: "text.disabled", bgcolor: "background.default" },
                  }}
                >
                  View
                  <Box component="svg" viewBox="0 0 16 16" aria-hidden sx={{ height: 12, width: 12 }}>
                    <path
                      d="M6 3l5 5-5 5"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </Box>
                </MuiLink>
              </TableCell>
            </TableRow>
          ))}
        </SupportTable>
      )}

      {conversationsQuery.data?.meta && (
        <Pagination
          page={conversationsQuery.data.meta.page}
          perPage={conversationsQuery.data.meta.per_page}
          totalCount={conversationsQuery.data.meta.total}
          totalPages={Math.max(
            1,
            Math.ceil(conversationsQuery.data.meta.total / conversationsQuery.data.meta.per_page),
          )}
          entityNoun="conversations"
          onPageChange={(p) => setParam("page", String(p))}
        />
      )}
    </Box>
  );
}

function ConversationStatusPill({ status }: { status: string }): JSX.Element {
  return (
    <Box
      component="span"
      sx={[
        {
          display: "inline-flex",
          alignItems: "center",
          borderRadius: 999,
          px: 1,
          py: 0.25,
          fontSize: 12,
          fontWeight: 500,
        },
        statusToneSx(status),
      ]}
    >
      {status}
    </Box>
  );
}

/** Status → soft tint. Mirrors the prior emerald/amber/sky/neutral set. */
function statusToneSx(status: string): SxStyle {
  switch (status) {
    case "active":
      return { bgcolor: (t: Theme) => alpha(t.palette.success.main, 0.1), color: "success.main" };
    case "flagged":
      return { bgcolor: (t: Theme) => alpha(t.palette.warning.main, 0.1), color: "warning.main" };
    case "escalated":
      return { bgcolor: (t: Theme) => alpha(t.palette.info.main, 0.1), color: "info.main" };
    case "archived":
    default:
      return { bgcolor: "action.hover", color: "text.disabled" };
  }
}

/** Visually-hidden but accessible (was Tailwind `sr-only`). */
const visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  borderWidth: 0,
} as const;

/** Inline search field. Commits on Enter so the URL doesn't churn on
 * every keystroke. */
function SearchInput({
  value,
  onCommit,
  placeholder,
}: {
  value: string;
  onCommit: (v: string) => void;
  placeholder: string;
}): JSX.Element {
  return (
    <TextField
      type="search"
      defaultValue={value}
      placeholder={placeholder}
      onKeyDown={(e) => {
        if (e.key === "Enter") onCommit((e.target as HTMLInputElement).value);
      }}
      inputProps={{ "aria-label": "Search conversation messages" }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Box component="svg" viewBox="0 0 16 16" aria-hidden sx={{ height: 16, width: 16, color: "text.disabled" }}>
              <path d="M11 11l3 3M7 12a5 5 0 100-10 5 5 0 000 10z" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
            </Box>
          </InputAdornment>
        ),
      }}
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 999, bgcolor: "surfaceSoft" } }}
    />
  );
}

function FilterSelect({
  value,
  onChange,
  placeholder,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
}): JSX.Element {
  return (
    <TextField
      select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      SelectProps={{ displayEmpty: true, "aria-label": placeholder }}
      sx={{ minWidth: 140, "& .MuiOutlinedInput-root": { borderRadius: 999, bgcolor: "surfaceSoft" } }}
    >
      <MenuItem value="">{placeholder}</MenuItem>
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
}
